import React, { Component } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  HiOutlineBookOpen,
  HiOutlineDocumentText,
  HiOutlineClock,
  HiOutlineFire,
  HiOutlineUserGroup,
  HiOutlineGlobeAlt,
  HiOutlineTag,
  HiOutlinePhoto,
  HiOutlineVideoCamera,
  HiOutlineArrowLeft,
  HiOutlineCheck,
  HiOutlineXMark,
  HiOutlineCloudArrowUp,
} from "react-icons/hi2";
import { countries } from "./dashboard/utils/countries";
import { recipeTypes } from "./dashboard/utils/recipeTypes";
import API from "../../config/api.js";
import { useLanguage } from "../../contexts/LanguageContext";
import { foodBg } from "../../assets/images";

/**
 * Page d'ajout de recette pour les utilisateurs connectés
 * Version orientée objet (class component) conservant le même comportement
 */
class AddRecipe extends Component {
  constructor(props) {
    super(props);
    this.state = {
      formData: {
        title: "",
        description: "",
        ingredients: "",
        instructions: "",
        prep_time: "",
        cook_time: "",
        servings: "",
        difficulty: "Facile",
        country: "",
        recipe_type: "",
        image_url: "",
        video_url: "",
      },
      loading: false,
      error: null,
      success: null,
      imagePreview: null,
      uploadingImage: false,
    };
  }

  /**
   * Gérer les changements dans le formulaire
   */
  handleChange = (event) => {
    const { name, value } = event.target;
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  };

  /**
   * Gérer le changement du champ URL d'image (avec mise à jour de l'aperçu)
   */
  handleImageUrlChange = (event) => {
    const { value } = event.target;
    this.handleChange(event);
    this.setState({ imagePreview: value || null });
  };

  /**
   * Gérer le téléchargement d'une image
   */
  handleImageUpload = (event) => {
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      this.setState({
        error: `❌ ${this.props.t("addRecipeSelectValidImage")}`,
      });
      return;
    }

    this.setState({ uploadingImage: true, error: null });

    const reader = new FileReader();

    reader.onloadend = () => {
      const base64String = reader.result;
      this.setState((prevState) => ({
        imagePreview: base64String,
        formData: {
          ...prevState.formData,
          image_url: base64String,
        },
        uploadingImage: false,
      }));
    };

    reader.onerror = () => {
      this.setState({
        error: `❌ ${this.props.t("addRecipeImageReadError")}`,
        uploadingImage: false,
      });
    };

    reader.readAsDataURL(file);
  };

  /**
   * Supprimer l'image
   */
  handleRemoveImage = () => {
    this.setState((prevState) => ({
      imagePreview: null,
      formData: {
        ...prevState.formData,
        image_url: "",
      },
    }));
  };

  /**
   * Gérer la soumission du formulaire
   */
  handleSubmit = async (event) => {
    event.preventDefault();
    this.setState({ error: null, success: null });

    const { formData } = this.state;

    if (!formData.title.trim()) {
      this.setState({ error: `❌ ${this.props.t("addRecipeTitleRequired")}` });
      return;
    }

    if (!formData.description.trim()) {
      this.setState({
        error: `❌ ${this.props.t("addRecipeDescriptionRequired")}`,
      });
      return;
    }

    if (!formData.ingredients.trim()) {
      this.setState({
        error: `❌ ${this.props.t("addRecipeIngredientsRequired")}`,
      });
      return;
    }

    if (!formData.instructions.trim()) {
      this.setState({
        error: `❌ ${this.props.t("addRecipeInstructionsRequired")}`,
      });
      return;
    }

    if (isNaN(formData.prep_time) || formData.prep_time < 0) {
      this.setState({
        error: `❌ ${this.props.t("addRecipePrepTimeInvalid")}`,
      });
      return;
    }

    if (isNaN(formData.cook_time) || formData.cook_time < 0) {
      this.setState({
        error: `❌ ${this.props.t("addRecipeCookTimeInvalid")}`,
      });
      return;
    }

    if (isNaN(formData.servings) || formData.servings < 1) {
      this.setState({
        error: `❌ ${this.props.t("addRecipeServingsInvalid")}`,
      });
      return;
    }

    if (formData.image_url && formData.image_url.trim()) {
      if (
        !formData.image_url.startsWith("data:image/") &&
        !formData.image_url.startsWith("http")
      ) {
        this.setState({
          error: `❌ ${this.props.t("addRecipeImageUrlInvalid")}`,
        });
        return;
      }
    }

    this.setState({ loading: true });

    try {
      const { token, navigate } = this.props;
      const dataToSend = {
        ...formData,
        prep_time: parseInt(formData.prep_time, 10) || 0,
        cook_time: parseInt(formData.cook_time, 10) || 0,
        servings: parseInt(formData.servings, 10) || 0,
      };

      console.log("📤 Données envoyées au backend :", dataToSend);
      console.log("📷 image_url :", dataToSend.image_url);
      console.log(
        "📷 Taille image_url :",
        dataToSend.image_url ? dataToSend.image_url.length : 0,
        "caractères",
      );
      console.log("📷 Type image_url :", typeof dataToSend.image_url);
      console.log("🎥 video_url :", dataToSend.video_url);

      const response = await fetch(`${API.baseURL}/recipes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(dataToSend),
      });

      console.log("📊 Statut de la réponse :", response.status);

      if (response.ok) {
        const data = await response.json();
        console.log("✅ Recette créée avec succès:", data);

        this.setState({
          success: `✅ ${this.props.t("addRecipeSuccess")}`,
          error: null,
        });

        setTimeout(() => {
          navigate("/dashboard");
        }, 2000);
      } else {
        const errorData = await response.json();
        console.error("❌ Erreur lors de la création:", errorData);
        console.error("❌ Détails de l'erreur:", errorData);
        this.setState({
          error: errorData.message || this.props.t("addRecipeErrorCreate"),
        });
      }
    } catch (error) {
      console.error("❌ Erreur:", error);
      this.setState({ error: this.props.t("addRecipeConnectionError") });
    } finally {
      this.setState({ loading: false });
    }
  };

  /**
   * Annuler et retourner au dashboard
   */
  handleCancel = () => {
    const { navigate } = this.props;
    navigate("/dashboard");
  };

  /**
   * Rediriger vers la page d'authentification
   */
  handleNavigateToAuth = () => {
    const { navigate } = this.props;
    navigate("/auth");
  };

  render() {
    const { user, t } = this.props;
    const { formData, loading, error, success, imagePreview, uploadingImage } =
      this.state;

    const inputClass =
      "w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/60 focus:border-orange-500 text-slate-800 dark:text-slate-100 placeholder-slate-400 text-sm transition-all";
    const labelClass =
      "block text-sm font-medium text-white/95 mb-2 flex items-center gap-2";

    if (!user) {
      return (
        <div className="relative min-h-screen w-full flex items-center justify-center">
          <div
            className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat bg-slate-900"
            style={{ backgroundImage: `url(${foodBg})` }}
            aria-hidden
          />
          <div className="absolute inset-0 z-0 bg-slate-900/50" aria-hidden />
          <motion.div
            className="relative z-10 text-center max-w-md mx-auto px-6 py-12 bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <h1 className="text-2xl font-bold text-white mb-3">
              {t("unauthorizedAccess") || "Accès non autorisé"}
            </h1>
            <p className="text-white/90 mb-6">{t("mustBeLoggedInToAdd")}</p>
            <button
              onClick={this.handleNavigateToAuth}
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-semibold transition-all shadow-lg"
            >
              {t("login") || "Se connecter"}
            </button>
          </motion.div>
        </div>
      );
    }

    return (
      <div className="relative min-h-screen w-full overflow-x-hidden">
        <div
          className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat bg-slate-900"
          style={{ backgroundImage: `url(${foodBg})` }}
          aria-hidden
        />
        <div className="fixed inset-0 z-0 bg-slate-900/45" aria-hidden />

        <div className="relative z-10 w-full min-w-0 py-6 sm:py-10 md:py-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* En-tête */}
            <motion.div
              className="relative bg-white/10 dark:bg-gray-800/50 backdrop-blur-lg rounded-2xl sm:rounded-3xl shadow-xl border border-slate-200/50 dark:border-slate-600/50 p-5 sm:p-6 mb-6"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              <div className="absolute left-0 top-0 bottom-0 w-1.5 sm:w-2 bg-gradient-to-b from-orange-500 to-amber-500 rounded-r-full" />
              <div className="pl-5 sm:pl-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
                    <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center text-white shadow-lg">
                      <HiOutlineBookOpen className="w-6 h-6" />
                    </span>
                    {t("addRecipe") || "Ajouter une recette"}
                  </h1>
                  <p className="text-white/85 mt-2 text-sm sm:text-base">
                    {t("addRecipeShareSubtitle")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={this.handleCancel}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition-all border border-white/20 shrink-0"
                >
                  <HiOutlineArrowLeft className="w-5 h-5" />
                  {t("addRecipeBack")}
                </button>
              </div>
            </motion.div>

            {/* Formulaire */}
            <motion.div
              className="relative bg-white/10 dark:bg-gray-800/50 backdrop-blur-lg rounded-2xl sm:rounded-3xl shadow-xl border border-slate-200/50 dark:border-slate-600/50 p-5 sm:p-6 md:p-8"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.05 }}
            >
              <div className="absolute left-0 top-0 bottom-0 w-1.5 sm:w-2 bg-gradient-to-b from-orange-500/80 to-amber-500/80 rounded-r-full" />
              <form
                onSubmit={this.handleSubmit}
                className="pl-4 sm:pl-5 space-y-6 sm:space-y-8"
              >
                {error && (
                  <div className="flex items-center gap-3 bg-red-500/20 border border-red-400/50 text-red-100 px-4 py-3 rounded-xl">
                    <HiOutlineXMark className="w-5 h-5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                {success && (
                  <div className="flex items-center gap-3 bg-emerald-500/20 border border-emerald-400/50 text-emerald-100 px-4 py-3 rounded-xl">
                    <HiOutlineCheck className="w-5 h-5 flex-shrink-0" />
                    <span>{success}</span>
                  </div>
                )}

                {/* Titre & Description */}
                <section className="space-y-4">
                  <h2 className="text-lg font-semibold text-white/95 flex items-center gap-2">
                    <HiOutlineDocumentText className="w-5 h-5 text-orange-300" />
                    {t("addRecipePresentation")}
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>
                        {t("addRecipeTitle")}
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={this.handleChange}
                        className={inputClass}
                        placeholder={t("addRecipeTitlePlaceholder")}
                        required
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Description</label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={this.handleChange}
                        rows="3"
                        className={inputClass + " resize-y min-h-[80px]"}
                        placeholder={t("addRecipeDescriptionPlaceholder")}
                      />
                    </div>
                  </div>
                </section>

                {/* Ingrédients & Instructions */}
                <section className="space-y-4">
                  <h2 className="text-lg font-semibold text-white/95 flex items-center gap-2">
                    <HiOutlineDocumentText className="w-5 h-5 text-orange-300" />
                    {t("addRecipeSection")}
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>{t("ingredients")} *</label>
                      <textarea
                        name="ingredients"
                        value={formData.ingredients}
                        onChange={this.handleChange}
                        rows="6"
                        className={inputClass + " font-mono text-sm resize-y"}
                        placeholder={t("addRecipeIngredientsPlaceholder")}
                        required
                      />
                    </div>
                    <div>
                      <label className={labelClass}>
                        {t("instructions")} *
                      </label>
                      <textarea
                        name="instructions"
                        value={formData.instructions}
                        onChange={this.handleChange}
                        rows="6"
                        className={inputClass + " resize-y"}
                        placeholder={t("addRecipeInstructionsPlaceholder")}
                        required
                      />
                    </div>
                  </div>
                </section>

                {/* Temps & Portions */}
                <section className="space-y-4">
                  <h2 className="text-lg font-semibold text-white/95 flex items-center gap-2">
                    <HiOutlineClock className="w-5 h-5 text-orange-300" />
                    {t("addRecipeTimePortions")}
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>
                        <HiOutlineClock className="w-4 h-4" />
                        {t("addRecipePrepMin")}
                      </label>
                      <input
                        type="number"
                        name="prep_time"
                        value={formData.prep_time}
                        onChange={this.handleChange}
                        className={inputClass}
                        placeholder="15"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>
                        <HiOutlineFire className="w-4 h-4" />
                        {t("addRecipeCookMin")}
                      </label>
                      <input
                        type="number"
                        name="cook_time"
                        value={formData.cook_time}
                        onChange={this.handleChange}
                        className={inputClass}
                        placeholder="20"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>
                        <HiOutlineUserGroup className="w-4 h-4" />
                        {t("servings")}
                      </label>
                      <input
                        type="number"
                        name="servings"
                        value={formData.servings}
                        onChange={this.handleChange}
                        className={inputClass}
                        placeholder="4"
                        min="1"
                      />
                    </div>
                  </div>
                </section>

                {/* Pays, Type, Difficulté */}
                <section className="space-y-4">
                  <h2 className="text-lg font-semibold text-white/95 flex items-center gap-2">
                    <HiOutlineGlobeAlt className="w-5 h-5 text-orange-300" />
                    {t("addRecipeClassification")}
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>
                        {t("addRecipeCountryOrigin")}
                      </label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={this.handleChange}
                        className={inputClass + " cursor-pointer"}
                      >
                        {countries.map((c) => (
                          <option key={c.code || "all"} value={c.code}>
                            {c.code ? t(c.nameKey) : t("addRecipeNotSpecified")}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>
                        <HiOutlineTag className="w-4 h-4" />
                        {t("addRecipeType")}
                      </label>
                      <select
                        name="recipe_type"
                        value={formData.recipe_type}
                        onChange={this.handleChange}
                        className={inputClass + " cursor-pointer"}
                      >
                        {recipeTypes.map((rt) => (
                          <option key={rt.value} value={rt.value}>
                            {t(rt.labelKey)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>
                        {t("difficultyLabel")}
                      </label>
                      <select
                        name="difficulty"
                        value={formData.difficulty}
                        onChange={this.handleChange}
                        className={inputClass + " cursor-pointer"}
                      >
                        <option value="Facile">{t("easy")}</option>
                        <option value="Moyen">{t("medium")}</option>
                        <option value="Difficile">{t("hard")}</option>
                      </select>
                    </div>
                  </div>
                </section>

                {/* Photo & Vidéo */}
                <section className="space-y-4">
                  <h2 className="text-lg font-semibold text-white/95 flex items-center gap-2">
                    <HiOutlinePhoto className="w-5 h-5 text-orange-300" />
                    {t("addRecipeMedia")}
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <label className={labelClass}>
                        {t("addRecipePhoto")}
                      </label>
                      {!imagePreview ? (
                        <>
                          <label className="block cursor-pointer">
                            <div className="border-2 border-dashed border-orange-400/60 rounded-xl p-6 hover:border-orange-500 hover:bg-white/5 transition-all text-center">
                              <HiOutlineCloudArrowUp className="w-10 h-10 text-orange-400/80 mx-auto mb-2" />
                              <p className="text-sm font-medium text-white/90">
                                {uploadingImage
                                  ? t("addRecipeUploading")
                                  : t("addRecipeClickOrDrag")}
                              </p>
                              <p className="text-xs text-white/60 mt-1">
                                PNG, JPG, GIF
                              </p>
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={this.handleImageUpload}
                              className="hidden"
                              disabled={uploadingImage}
                            />
                          </label>
                          <p className="text-xs text-white/60 text-center">
                            {t("addRecipeOr")}
                          </p>
                          <input
                            type="text"
                            name="image_url"
                            value={formData.image_url}
                            onChange={this.handleImageUrlChange}
                            className={inputClass + " text-sm"}
                            placeholder={t("addRecipeImageUrlPlaceholder")}
                          />
                        </>
                      ) : (
                        <div className="relative rounded-xl overflow-hidden border border-white/20">
                          <img
                            src={imagePreview}
                            alt="Aperçu"
                            className="w-full h-48 object-cover"
                            onError={(e) => {
                              e.target.src =
                                "https://via.placeholder.com/400x300?text=Erreur";
                            }}
                          />
                          <button
                            type="button"
                            onClick={this.handleRemoveImage}
                            className="absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center justify-center transition-colors shadow-lg"
                            title={t("addRecipeDelete")}
                          >
                            <HiOutlineXMark className="w-5 h-5" />
                          </button>
                          <p className="absolute bottom-2 left-2 text-xs text-white/90 bg-black/40 px-2 py-1 rounded">
                            Image chargée
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      <label className={labelClass}>
                        <HiOutlineVideoCamera className="w-4 h-4" />
                        {t("addRecipeVideoLink")}
                      </label>
                      <input
                        type="url"
                        name="video_url"
                        value={formData.video_url}
                        onChange={this.handleChange}
                        className={inputClass}
                        placeholder={t("addRecipeVideoPlaceholder")}
                      />
                      {formData.video_url && (
                        <p
                          className="text-xs text-white/70 truncate"
                          title={formData.video_url}
                        >
                          {formData.video_url}
                        </p>
                      )}
                    </div>
                  </div>
                </section>

                {/* Actions */}
                <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-white/15">
                  <button
                    type="button"
                    onClick={this.handleCancel}
                    className="flex-1 px-5 py-3.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold border border-white/20 transition-all disabled:opacity-50"
                    disabled={loading}
                  >
                    {t("cancel") || "Annuler"}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-5 py-3.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-orange-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                        {t("addRecipeCreating")}
                      </>
                    ) : (
                      <>
                        <HiOutlineCheck className="w-5 h-5" />
                        {t("addRecipeCreate")}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }
}

const AddRecipeWithNavigation = (props) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  return <AddRecipe {...props} navigate={navigate} t={t} />;
};

export default AddRecipeWithNavigation;
